import { showSuccess } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { shareOrDownloadCsv, shareOrDownloadTxt } from '../../shared/csv-core/infra/shareCsv';
import { getFiscalContext } from '../infra/fiscalConfigRepo';
import {
  exportLibroIvaComprasCsv,
  exportLibroIvaVentasCsv,
  fetchLibroIvaComprasRows,
  fetchLibroIvaVentasRows,
} from '../infra/fiscalRepository';
import { buildVentasAlicuotasTxt, buildVentasCbteTxt } from '../domain/libroIvaTxt';
import { buildComprasAlicuotasTxt, buildComprasCbteTxt } from '../domain/libroIvaComprasTxt';
import { downloadLibroIvaControlPdf } from './libroIvaControlPdfExport';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export async function handleLibroIvaExport(branchId: string | null | undefined): Promise<void> {
  if (!branchId) return;
  try {
    const csv = await exportLibroIvaVentasCsv(branchId);
    const filename = `libro-iva-ventas-${new Date().getFullYear()}.csv`;
    const outcome = await shareOrDownloadCsv(csv, filename);
    if (outcome === 'ok') {
      showSuccess('Exportado', 'Se descargó el archivo CSV.');
    }
  } catch (err: unknown) {
    if (err instanceof Error && err.message.includes('cancel')) return;
    presentUnexpectedFailure({ module: 'fiscal', op: 'error', error: err, title: 'Error', userMessage: 'No se pudo exportar el libro IVA.' });
  }
}

const monthBoundsIso = (year: number, month: number): { fromIso: string; toIso: string } => {
  const from = new Date(Date.UTC(year, month - 1, 1));
  const to = new Date(Date.UTC(year, month, 1));
  return { fromIso: from.toISOString(), toIso: to.toISOString() };
};

const monthBoundsDate = (year: number, month: number): { fromDate: string; toDate: string } => {
  const fromDate = `${year}-${String(month).padStart(2, '0')}-01`;
  const toDate = `${month === 12 ? year + 1 : year}-${String(month === 12 ? 1 : month + 1).padStart(2, '0')}-01`;
  return { fromDate, toDate };
};

const periodTag = (year: number, month: number) => `${year}${String(month).padStart(2, '0')}`;

/**
 * Libro IVA Digital oficial — 2 archivos de ancho fijo del período mensual:
 * `VENTAS_CBTE` (comprobantes, 266 pos) + `VENTAS_ALICUOTAS` (62 pos).
 * Se comparten/descargan en secuencia.
 */
export async function handleLibroIvaTxtExport(
  branchId: string | null | undefined,
  year: number,
  month: number
): Promise<void> {
  if (!branchId) return;
  try {
    const { fromIso, toIso } = monthBoundsIso(year, month);
    const rows = await fetchLibroIvaVentasRows(branchId, fromIso, toIso);
    if (rows.length === 0) {
      showSuccess('Sin comprobantes', 'No hay comprobantes fiscales en el período elegido.');
      return;
    }
    const tag = `${year}${String(month).padStart(2, '0')}`;
    const cbteTxt = buildVentasCbteTxt(rows);
    const alicuotasTxt = buildVentasAlicuotasTxt(rows);
    const first = await shareOrDownloadTxt(cbteTxt, `LIBRO_IVA_DIGITAL_VENTAS_CBTE_${tag}.txt`);
    if (first !== 'ok') return;
    const second = await shareOrDownloadTxt(alicuotasTxt, `LIBRO_IVA_DIGITAL_VENTAS_ALICUOTAS_${tag}.txt`);
    if (second === 'ok') {
      showSuccess('Exportado', 'Se generaron los 2 archivos del Libro IVA Digital.');
    }
  } catch (err: unknown) {
    if (err instanceof Error && err.message.includes('cancel')) return;
    presentUnexpectedFailure({
      module: 'fiscal',
      op: 'libro-iva-ventas-txt.export',
      error: err,
      title: 'Error',
      userMessage: 'No se pudo exportar el Libro IVA Digital.',
    });
  }
}

/**
 * Libro IVA Digital COMPRAS — 2 archivos oficiales del período mensual:
 * `COMPRAS_CBTE` (325 pos) + `COMPRAS_ALICUOTAS` (84 pos).
 * Fuente: `pos.expenses` con bloque fiscal cargado (solo RI lo captura).
 */
export async function handleLibroIvaComprasCsvExport(
  branchId: string | null | undefined,
  year: number,
  month: number
): Promise<void> {
  if (!branchId) return;
  try {
    const { fromDate, toDate } = monthBoundsDate(year, month);
    const csv = await exportLibroIvaComprasCsv(branchId, fromDate, toDate);
    const filename = `libro-iva-compras-${periodTag(year, month)}.csv`;
    const outcome = await shareOrDownloadCsv(csv, filename);
    if (outcome === 'ok') {
      showSuccess('Exportado', 'Se descargó el CSV de compras del mes.');
    }
  } catch (err: unknown) {
    if (err instanceof Error && err.message.includes('cancel')) return;
    presentUnexpectedFailure({
      module: 'fiscal',
      op: 'libro-iva-compras-csv.export',
      error: err,
      title: 'Error',
      userMessage: 'No se pudo exportar el resumen de compras.',
    });
  }
}

export async function handleLibroIvaControlPdfExport(
  branchId: string | null | undefined,
  year: number,
  month: number
): Promise<void> {
  if (!branchId) return;
  try {
    const { fromIso, toIso } = monthBoundsIso(year, month);
    const { fromDate, toDate } = monthBoundsDate(year, month);
    const [ventas, compras, ctx] = await Promise.all([
      fetchLibroIvaVentasRows(branchId, fromIso, toIso),
      fetchLibroIvaComprasRows(branchId, fromDate, toDate),
      getFiscalContext(branchId).catch(() => null),
    ]);
    if (ventas.length === 0 && compras.length === 0) {
      showSuccess('Sin comprobantes', 'No hay ventas ni compras fiscales en el período elegido.');
      return;
    }
    const profile = ctx?.profile;
    const emitterBusinessName = profile?.businessName?.trim() || 'Emisor';
    const emitterCuit = profile?.emitterCuit?.trim() || '';
    const branchLabel =
      ctx?.branchesFiscal?.find((b) => b.branchId === branchId)?.branchName ?? null;
    await downloadLibroIvaControlPdf({
      periodLabel: `${MONTH_NAMES[month - 1]} ${year}`,
      periodTag: periodTag(year, month),
      emitterBusinessName,
      emitterCuit,
      branchLabel,
      ventas,
      compras,
    });
    showSuccess('Exportado', 'Se generó el PDF de control del mes.');
  } catch (err: unknown) {
    if (err instanceof Error && err.message.includes('cancel')) return;
    presentUnexpectedFailure({
      module: 'fiscal',
      op: 'libro-iva-control-pdf.export',
      error: err,
      title: 'Error',
      userMessage: 'No se pudo generar el PDF de control.',
    });
  }
}

export async function handleLibroIvaComprasTxtExport(
  branchId: string | null | undefined,
  year: number,
  month: number
): Promise<void> {
  if (!branchId) return;
  try {
    const { fromDate, toDate } = monthBoundsDate(year, month);
    const rows = await fetchLibroIvaComprasRows(branchId, fromDate, toDate);
    if (rows.length === 0) {
      showSuccess('Sin comprobantes', 'No hay comprobantes de compras con datos fiscales en el período elegido.');
      return;
    }
    const tag = `${year}${String(month).padStart(2, '0')}`;
    const cbteTxt = buildComprasCbteTxt(rows);
    const alicuotasTxt = buildComprasAlicuotasTxt(rows);
    const first = await shareOrDownloadTxt(cbteTxt, `LIBRO_IVA_DIGITAL_COMPRAS_CBTE_${tag}.txt`);
    if (first !== 'ok') return;
    const second = await shareOrDownloadTxt(alicuotasTxt, `LIBRO_IVA_DIGITAL_COMPRAS_ALICUOTAS_${tag}.txt`);
    if (second === 'ok') {
      showSuccess('Exportado', 'Se generaron los 2 archivos del Libro IVA compras.');
    }
  } catch (err: unknown) {
    if (err instanceof Error && err.message.includes('cancel')) return;
    presentUnexpectedFailure({
      module: 'fiscal',
      op: 'libro-iva-compras-txt.export',
      error: err,
      title: 'Error',
      userMessage: 'No se pudo exportar el Libro IVA compras.',
    });
  }
}
